<template>
  <b-row class="justify-content-md-center m-0">
    <b-col lg="8" class="my-3 p-0 page__preview-evidence">
      <b-col xl="10" class="p-0 card__preview-evidence">
        <b-row class="justify-content-center m-0">
          <b-col class="mt-3">
            <base-bread-crumb :items="breadcrumbs" />

            <h5 class="display-4 font-weight-bold header__preview-title">Preview Submission</h5>
            <h6 v-if="learningUnit" class="header__lu-text mb-3">
              {{ learningUnit.name }}
            </h6>
          </b-col>
        </b-row>

        <b-row class="justify-content-center m-0">
          <div v-if="loading" class="d-flex justify-content-center">
            <b-spinner label="Loading..." variant="dark" />
          </div>

          <b-col v-else class="mb-3">
            <div v-if="evidenceSubmission.isAwarded && credential" class="mb-4">
              <modal-congratulation
                :show="credential.seen_at === null ? true : false"
                :accredible-link="credential.accredible_credential.url"
                :credential-id="credential.id"
              />

              <post-certificate
                :certificate-image-link="learningUnit.badge_image[0].url"
                :accredible-link="credential.accredible_credential.url"
                :user="credential.user"
                :date="new Date(credential.accredible_credential.issued_on)"
              />
            </div>

            <b-row
              v-if="evidenceSubmission.isAwarded && evidenceSubmission.assessor_recommendation"
              class="mb-4"
            >
              <b-col cols="12">
                <b-card no-body class="card-box">
                  <b-card-body>
                    <div class="font-weight-bold mb-1 title__recommendation mb-3">
                      Assessor's recommendation
                    </div>

                    <div
                      v-safe-html="evidenceSubmission.assessor_recommendation"
                      class="border border-medium rounded p-3 text-pre-wrap text__recommendation bg-secondary"
                    ></div>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <learning-unit-score-description-card
              v-if="learningUnit.score_description"
              :learning-unit="learningUnit"
              class="mb-4"
            />

            <b-form id="formSubmitEvidence" @submit.prevent="onSubmit">
              <post-evaluation-assessment-part
                v-for="(partMeta, index) in partMetaData"
                :key="`card__part-${index + 1}-task-list`"
                class="mb-3"
                :part="partMeta"
                :learning-unit="learningUnit"
                :post-ids="partPostIds[partMeta] ? partPostIds[partMeta] : []"
                :editable="false"
                submittable
                :readonly="true"
                mode="preview"
                :assessable="evidenceSubmission.isAwarded || evidenceSubmission.isDenied"
                :disabled="submitting"
                :evidence-submission="evidenceSubmission"
              >
              </post-evaluation-assessment-part>

              <b-row v-if="evidenceSubmission.isNotSubmitted">
                <b-col cols="12">
                  <base-card-indicator variant="info">
                    <h5 class="font-weight-bold text__consent-title mb-0">
                      Student Privacy and Academic Integrity Statement
                    </h5>
                  </base-card-indicator>
                </b-col>

                <b-col cols="12" class="mt-3">
                  <b-card no-body class="card-box">
                    <b-card-body class="pt-2">
                      <consent-agreement-form
                        v-model="isAgreed"
                        topic="kmutt4life__agreement-submit-evidence"
                        :cache-key="`${evidenceSubmission.id}`"
                        :agreements="agreements"
                        :disabled="submitting"
                        :is-save-agreement="false"
                      ></consent-agreement-form>
                    </b-card-body>

                    <b-card-footer>
                      <b-button
                        variant="success"
                        :disabled="!canSubmit || submitting"
                        @click="activeModal = true"
                      >
                        <span>Submit</span>
                      </b-button>
                      <b-button variant="link" class="ml-u1" @click="goToPreviousPage">
                        Edit Submission
                      </b-button>
                    </b-card-footer>
                  </b-card>
                </b-col>

                <!-- Confirm modal -->
                <base-modal :show.sync="activeModal">
                  <template #modal-header>
                    <h5>Submit Evidence</h5>
                  </template>

                  <div v-if="hasInvalidUploadFiles" class="confirmation__invalid">
                    <b-alert variant="warning" show class="mb-0">
                      <div class="font-weight-bold">
                        There are invalid upload evidences in this assessment. Please remove them
                        before continue to submit.
                      </div>
                    </b-alert>
                  </div>

                  <div v-else class="confirmation__valid">
                    <div class="text-black-50 mb-2">
                      Are you sure you want to submit. After confirmation you can't edit this
                      submission anymore, and you will
                      <span v-if="remainingSubmission > 0">have</span>
                      <span
                        class="text-danger"
                        v-text="
                          remainingSubmission > 0
                            ? ` ${remainingSubmission} submission quota remaining.`
                            : 'reach submission quota limit'
                        "
                      >
                      </span>
                      <span v-if="remainingSubmission === 0">
                        but you have the option to buy more.</span
                      >
                    </div>

                    <b-alert variant="primary" show>
                      <div class="font-weight-bold">
                        <b>{{ learningUnit.name }}</b>
                      </div>
                    </b-alert>

                    <div class="text-black-50">
                      Click at the "Confirm" button to submit this micro-credential.
                    </div>
                  </div>

                  <template #modal-footer>
                    <div v-if="hasInvalidUploadFiles" class="d-flex">
                      <b-button variant="link" class="btn-link-dark" @click="activeModal = false">
                        OK
                      </b-button>
                    </div>

                    <div v-else class="d-flex">
                      <b-button
                        variant="link"
                        class="btn-link-dark"
                        :disabled="submitting"
                        @click="activeModal = false"
                      >
                        Cancel
                      </b-button>

                      <b-button type="submit" variant="success" :disabled="submitting">
                        <span v-if="!submitting">Confirm</span>

                        <span v-else class="pr-2 pl-2">
                          <b-spinner label="Loading..." variant="secondary" small />
                        </span>
                      </b-button>
                    </div>
                  </template>
                </base-modal>
              </b-row>
            </b-form>

            <b-row v-if="evidenceSubmission.isDenied || evidenceSubmission.isAwarded">
              <b-col cols="12">
                <b-card no-body class="card-box">
                  <b-card-body class="pt-3">
                    <div>
                      <div class="font-weight-bold mb-1 title__feedback-for-earner mb-3">
                        Feedback from Issuer
                      </div>

                      <div
                        v-safe-html="evidenceSubmission.feedback_for_earner"
                        class="border border-medium rounded p-3 text-pre-wrap text__feedback-for-earner bg-secondary"
                      ></div>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-col>
    </b-col>
  </b-row>
</template>

<script>
import { usePostExtractor } from '@/composables/usePostExtractor';
import { feedMixin } from '@/mixins/feedMixin';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import LearningUnit from '@/models/LearningUnit';
import Application from '@/models/Application';
import BaseCardIndicator from '@/components/base-components/BaseCardIndicator';
import PostEvaluationAssessmentPart from '@/components/Post/PostEvaluationAssessmentPart';
import ConsentAgreementForm from '@/components/Consent/ConsentAgreementForm';
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb';

import PostCertificate from '@/views/Feed/components/PostCertificate';
import ModalCongratulation from '@/views/Feed/components/ModalCongratulation';
import Credential from '@/models/Credential';
import Post from '@/models/Post';
import { userInfoMixin } from '@/mixins';
import BaseModal from '@/components/base-components/BaseModal';
import LearningUnitScoreDescriptionCard from '@/components/LearningUnit/LearningUnitScoreDescriptionCard';

export default {
  components: {
    BaseCardIndicator,
    PostEvaluationAssessmentPart,
    ConsentAgreementForm,
    BaseBreadCrumb,
    PostCertificate,
    ModalCongratulation,
    BaseModal,
    LearningUnitScoreDescriptionCard
  },
  mixins: [feedMixin, userInfoMixin],

  data() {
    return {
      loading: false,
      isAgreed: false,
      submitting: false,
      activeModal: false,
      agreements: [
        {
          key: 'READY_FOR_REVIEW',
          title: 'Ready for Review',
          description:
            'I confirm that my application is ready for review and I have set permissions appropriately for my evidence.'
        },
        {
          key: 'CONFIRM_RELEASE',
          title: 'Confirm Release',
          description:
            'I confirm that this submission does not include any personally identifiable data and does not violate any laws.'
        },
        {
          key: 'CONFIRM_WORK',
          title: 'Confirm Work',
          description:
            'I confirm that this submission does not contain plagiarism and is my own original work. I have included appropriate citations and credits in compliance with any applicable academic integrity policy. I understand that if plagiarism or any violations are found, KMUTT may report this submission or take any other action permitted by KMUTT’s regulations.'
        }
      ],
      selectedEvidenceSubmissionId: this.$route.query.submissionId || null
    };
  },

  computed: {
    learningUnitId() {
      return Number.parseInt(this.$route.params.learningUnitId);
    },

    learningUnit() {
      return LearningUnit.query()
        .with('ultimate_learning_outcome')
        .whereId(this.learningUnitId)
        .first();
    },

    partMetaData() {
      return Object.keys(Post.PART);
    },

    evidenceSubmission() {
      return this.selectedEvidenceSubmissionId
        ? EvidenceSubmission.find(this.selectedEvidenceSubmissionId)
        : null;
    },

    postEvaluationAssessments() {
      const posts = Post.query()
        .where('post_type', 'EVALUATION_ASSESSMENT')
        .where('learning_unit_id', this.learningUnitId)
        .get();

      return posts;
    },

    partPostIds() {
      const { extractPostIdGroupByPart } = usePostExtractor();

      const { postEvaluationAssessments = [] } = this;

      return postEvaluationAssessments.length
        ? extractPostIdGroupByPart(postEvaluationAssessments)
        : {};
    },

    canSubmit() {
      return this.evidenceSubmission ? this.evidenceSubmission.canSubmit && this.isAgreed : false;
    },

    hasInvalidUploadFiles() {
      return this.evidenceSubmission ? this.evidenceSubmission.hasInvalidUploadFiles : false;
    },

    credential() {
      return Credential.query()
        .where('learning_unit_id', this.learningUnitId)
        .where('user_id', this.userId)
        .with('user')
        .first();
    },
    breadcrumbs() {
      const { learningUnit } = this;

      const { $route } = this;

      const breadcrumb = [
        {
          key: 'user-dashboard',
          name: 'User Dashboard',
          to: {
            name: 'user-dashboard'
          }
        },
        {
          key: 'micro-credentials-detail',
          name: learningUnit ? learningUnit.name : '',
          to: {
            name: 'micro-credentials/detail',
            params: {
              learningUnitId: learningUnit ? learningUnit.id : null
            }
          }
        }
      ];

      if ($route.name === 'learning-unit/verify') {
        breadcrumb.push({
          key: 'verify',
          name: 'Verify',
          active: true
        });
      }
      if ($route.name === 'learning-unit/preview') {
        breadcrumb.push({
          key: 'preview',
          name: 'preview',
          active: true
        });
      }

      if ($route.name === 'learning-unit/feed') {
        breadcrumb.push({
          key: 'learning-feed',
          name: 'Learning',
          active: true
        });
      }

      return breadcrumb;
    },
    remainingSubmission() {
      return (
        Application.query()
          .where((apply) => {
            return apply.user.id == this.user.id && apply.learning_unit.id == this.learningUnitId;
          })
          .first()?.remaining_submission || 0
      );
    }
  },

  watch: {
    // "$route.path": {
    //   immediate: true,
    //   handler: "onRouteChanged",
    // },
    // selectedEvidenceSubmissionId: {
    //   handler: "onSelectedEvidenceSubmissionIdChanged",
    // },
  },

  async created() {
    await this.fetch();
  },

  methods: {
    // async onRouteChanged() {
    //   await this.fetch();
    // },

    onSelectedEvidenceSubmissionIdChanged(submissionId) {
      this.$router.push({
        query: {
          tab: 'submission',
          submissionId
        }
      });
    },

    async fetch() {
      this.loading = true;
      await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);

      await this.fetchCredential();

      try {
        await EvidenceSubmission.api().fetchLearner(this.learningUnitId);

        if (!this.$route.query.submissionId) {
          const latestEvidenceSubmission = EvidenceSubmission.getLatestEvidenceSubmission(
            this.learningUnitId,
            this.userId
          );

          if (latestEvidenceSubmission) {
            this.selectedEvidenceSubmissionId = latestEvidenceSubmission.id;
          }
        }
      } catch (error) {
        this.$toast('Cannot load your submissions, please try again');
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }
    },

    async fetchCredential() {
      let promise;

      try {
        promise = await Credential.api().fetch(this.learningUnitId, this.userId);
      } catch (error) {
        this.$toast.error('Cannot load credential');
      }

      return promise;
    },

    async onSubmit() {
      this.submitting = true;

      try {
        await EvidenceSubmission.api().submit(this.evidenceSubmission.id);

        this.$toast.success('Your evidence has been submitted successfully');
        this.activeModal = false;
        this.$router.push({
          name: 'learning-unit/verify',
          query: {
            tab: 'submission'
          }
        });
      } catch (error) {
        this.$toast.error('Cannot submit your evidence, please try again');
      } finally {
        this.submitting = false;
      }
    },
    goToPreviousPage() {
      if (this.$route.query.previousPath) {
        const { previousPath } = this.$route.query;

        this.$router.push({ path: previousPath });
      } else {
        this.$router.go(-1);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.page__preview-evidence {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.card-box {
  max-width: 910px;
  margin: auto;
}
.text__feedback-for-earner {
  img {
    max-width: -moz-available; /* WebKit-based browsers will ignore this. */
    max-width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
  }
}

::v-deep img.max-w-full,
::v-deep figure img {
  width: 100%;
  height: auto;
}
</style>
